/**
 * KanbanBoard — the top-level board orchestrator.
 *
 * Responsibilities:
 *   1. Fetch columns + cards via TanStack Query (single query each).
 *   2. Subscribe to /ws/boards/:boardId/events via useBoardEvents.
 *      Server pushes invalidate queries → TanStack refetches.
 *   3. Render horizontal scroller of KanbanColumns wrapped in a single
 *      dnd-kit DndContext + SortableContext.
 *   4. On drag-end, run an optimistic update (move the card in the
 *      cache + call /move endpoint) with rollback on failure.
 *   5. Expose inline column-add at the end of the row.
 *
 * Optimistic-update strategy (ADR-042 quality bar):
 *   - `onDragEnd` snapshots the current cards list.
 *   - It computes the new `column_id` + `position` from drop targets,
 *     mutates the cache synchronously so the drag feels instant.
 *   - It calls `moveCard()`; on success the server NOTIFY fan-out will
 *     invalidate the cache again, so the optimistic mutation gets
 *     reconciled with authoritative state.
 *   - On error, the snapshot is restored and a toast is shown.
 */

import type { Card, Column } from '@chaos/types/kanban';
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { positionForDrop } from '../../lib/kanban/fractional';
import { useBoardEvents } from '../../lib/kanban/use-board-events';
import { useAuthStore } from '../../stores/auth-store';
import { KanbanCard } from './kanban-card';
import { KanbanColumn } from './kanban-column';

interface KanbanBoardProps {
  boardId: string;
}

const ALL_WRITE_ROLES = new Set(['founder', 'admin', 'employee', 'tuner']);

interface CardsByColumn {
  byColumn: Map<string, Card[]>;
  byId: Map<string, Card>;
}

function indexCards(cards: Card[]): CardsByColumn {
  const byColumn = new Map<string, Card[]>();
  const byId = new Map<string, Card>();
  for (const c of cards) byId.set(c.id, c);
  // Group then sort by position; stable across renders.
  for (const c of cards) {
    const arr = byColumn.get(c.columnId) ?? [];
    arr.push(c);
    byColumn.set(c.columnId, arr);
  }
  for (const [, arr] of byColumn) arr.sort((a, b) => a.position - b.position);
  return { byColumn, byId };
}

export function KanbanBoard({ boardId }: KanbanBoardProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const role = useAuthStore(
    (s) => s.workspaces.find((w) => w.workspace.id === s.activeWorkspaceId)?.membership.role,
  );
  const canMutate = role ? ALL_WRITE_ROLES.has(role) || role === 'external' : false;
  const canMutateStructure = role ? ALL_WRITE_ROLES.has(role) : false;

  useBoardEvents(boardId);

  const columnsQ = useQuery({
    queryKey: kanbanKeys.boardColumns(boardId),
    queryFn: () => kanbanApi.listColumns(boardId),
  });
  const cardsQ = useQuery({
    queryKey: kanbanKeys.boardCards(boardId),
    queryFn: () => kanbanApi.listCards(boardId),
  });

  const columns = columnsQ.data ?? [];
  const cards = cardsQ.data ?? [];
  const grouped = React.useMemo(() => indexCards(cards), [cards]);

  // Drag state for the overlay (so the dragged card stays at the cursor).
  const [activeCard, setActiveCard] = React.useState<Card | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 6px of movement before drag starts so simple clicks navigate.
      activationConstraint: { distance: 6 },
    }),
  );

  const createCardM = useMutation({
    mutationFn: ({ columnId, title }: { columnId: string; title: string }) => {
      // Founder mandate (2026-05-15 round 5): new cards go to the TOP of
      // the column so the freshly-added work surfaces at the user's eye
      // line rather than at the bottom of a long list. Compute a position
      // strictly less than the current first card; empty column → 0.
      const existing = (grouped.byColumn.get(columnId) ?? []).map((c) => c.position);
      const minPos = existing.length === 0 ? 1 : Math.min(...existing);
      const position = positionForDrop(undefined, minPos);
      return kanbanApi.createCard({ boardId, columnId, title, position });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  const createColumnM = useMutation({
    mutationFn: (name: string) => kanbanApi.createColumn(boardId, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardColumns(boardId) });
    },
  });

  const deleteColumnM = useMutation({
    mutationFn: (columnId: string) => kanbanApi.deleteColumn(columnId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardColumns(boardId) });
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  const moveCardM = useMutation({
    mutationFn: ({
      cardId,
      columnId,
      position,
    }: {
      cardId: string;
      columnId: string;
      position: number;
    }) => kanbanApi.moveCard(cardId, { columnId, position }),
    onMutate: async ({ cardId, columnId, position }) => {
      await qc.cancelQueries({ queryKey: kanbanKeys.boardCards(boardId) });
      const prev = qc.getQueryData<Card[]>(kanbanKeys.boardCards(boardId));
      if (prev) {
        const next = prev.map((c) => (c.id === cardId ? { ...c, columnId, position } : c));
        qc.setQueryData(kanbanKeys.boardCards(boardId), next);
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(kanbanKeys.boardCards(boardId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardCards(boardId) });
    },
  });

  const handleDragStart = (event: DragStartEvent): void => {
    const id = String(event.active.id);
    const card = grouped.byId.get(id) ?? null;
    setActiveCard(card);
  };

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    setActiveCard(null);
    if (!over) return;
    const card = grouped.byId.get(String(active.id));
    if (!card) return;

    // Determine target column:
    //   - over.id matches a card id  → drop on that card's column
    //   - over.id matches a column id → empty drop or end-of-column
    const overId = String(over.id);
    const overCard = grouped.byId.get(overId);
    const targetColumnId = overCard ? overCard.columnId : overId;

    const targetCards = grouped.byColumn.get(targetColumnId) ?? [];
    // Strip the active card from the list when moving within the same column.
    const without = targetCards.filter((c) => c.id !== card.id);

    let newPosition: number;
    if (overCard) {
      // Drop on/near another card; compute neighbour positions.
      const idx = without.findIndex((c) => c.id === overCard.id);
      // dnd-kit doesn't tell us above-or-below; we approximate "before"
      // when the active was originally lower in the list, "after" otherwise.
      // For Phase 2 simplicity: drop between (idx-1) and idx of the
      // filtered list.
      const before = without[idx - 1]?.position;
      const after = without[idx]?.position;
      newPosition = positionForDrop(before, after);
    } else {
      // Empty / end-of-column drop: append.
      const last = without[without.length - 1]?.position;
      newPosition = positionForDrop(last, undefined);
    }

    if (card.columnId === targetColumnId && card.position === newPosition) return;
    moveCardM.mutate({
      cardId: card.id,
      columnId: targetColumnId,
      position: newPosition,
    });
  };

  // ── Inline column-add UI state ───────────────────────────────────
  const [addingColumn, setAddingColumn] = React.useState(false);
  const [columnDraft, setColumnDraft] = React.useState('');
  const columnInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (addingColumn) columnInputRef.current?.focus();
  }, [addingColumn]);

  const submitColumn = async (): Promise<void> => {
    const trimmed = columnDraft.trim();
    if (!trimmed) {
      setAddingColumn(false);
      return;
    }
    await createColumnM.mutateAsync(trimmed);
    setColumnDraft('');
    setAddingColumn(false);
  };

  if (columnsQ.isLoading || cardsQ.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  if (columnsQ.error || cardsQ.error) {
    return <div className="px-8 py-6 text-sm text-error">{t('kanban.error_loading')}</div>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToFirstScrollableAncestor]}
    >
      <div className="flex h-full gap-4 overflow-x-auto overflow-y-hidden px-6 pb-4">
        {columns.map((col: Column) => (
          <KanbanColumn
            key={col.id}
            column={col}
            cards={grouped.byColumn.get(col.id) ?? []}
            boardId={boardId}
            canMutate={canMutate}
            {...(canMutateStructure
              ? {
                  onDeleteColumn: async (id: string) => {
                    await deleteColumnM.mutateAsync(id);
                  },
                }
              : {})}
            onAddCard={async (columnId, title) => {
              await createCardM.mutateAsync({ columnId, title });
            }}
          />
        ))}

        {canMutateStructure && (
          <div className="w-64 shrink-0">
            {addingColumn ? (
              <div className="flex flex-col gap-1 px-2 pt-1">
                <input
                  ref={columnInputRef}
                  value={columnDraft}
                  onChange={(e) => setColumnDraft(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      await submitColumn();
                    }
                    if (e.key === 'Escape') {
                      setColumnDraft('');
                      setAddingColumn(false);
                    }
                  }}
                  onBlur={() => {
                    if (!columnDraft.trim()) setAddingColumn(false);
                  }}
                  placeholder={t('kanban.new_column_placeholder')}
                  className="w-full bg-transparent text-[13px] font-semibold tracking-tight outline-none placeholder:font-normal placeholder:text-muted-foreground"
                  aria-label={t('kanban.new_column_placeholder')}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAddingColumn(true)}
                className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('kanban.add_column')}
              </button>
            )}
          </div>
        )}
      </div>

      <DragOverlay>
        {activeCard ? <KanbanCard card={activeCard} boardId={boardId} isOverlay /> : null}
      </DragOverlay>
    </DndContext>
  );
}
